import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Filter, FileText, Edit, Trash2, Eye, Users } from "lucide-react";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { salesAPI } from "@/lib/api";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import type { HubFilters } from "@/features/sales/hubFilters";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface Invoice {
	id: string;
	invoiceNumber: string;
	customerName: string;
	customer?: {
		id: string;
		firstName: string;
		lastName: string;
	};
	amount: number;
	totalAmount?: number;
	status: "paid" | "pending" | "partial" | "overdue" | "cancelled";
	paymentStatus?: "paid" | "pending" | "partial" | "overdue";
	dueDate: string;
	createdAt: string;
	splitCount?: number;
}

const InvoicesPage: React.FC<{ filters?: HubFilters }> = ({ filters }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const currency = useCurrency();
	const { isRTL } = useLanguage();
	const { toast } = useToast();

	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalInvoices, setTotalInvoices] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// When embedded in the sales hub, the hub toolbar owns the filters
	useEffect(() => {
		if (!filters) return;
		setSearchTerm(filters.search);
		setSelectedStatus(filters.status);
		setDateFrom(filters.dateFrom);
		setDateTo(filters.dateTo);
		setCurrentPage(1);
	}, [filters]);

	useEffect(() => {
		fetchInvoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters change
	}, [currentPage, itemsPerPage, searchTerm, selectedStatus, dateFrom, dateTo]);

	const fetchInvoices = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const params: any = {
				page: currentPage,
				limit: itemsPerPage,
				groupSplits: true,
			};

			if (searchTerm) {
				params.search = searchTerm;
			}
			if (selectedStatus) {
				if (selectedStatus === "paid") {
					params.paymentStatus = "paid";
				} else if (selectedStatus === "partial") {
					params.paymentStatus = "partial";
				} else if (selectedStatus === "pending") {
					params.paymentStatus = "pending";
				} else if (selectedStatus === "overdue") {
					params.paymentStatus = "overdue";
				} else if (selectedStatus === "cancelled") {
					params.status = "cancelled";
				}
			}
			if (dateFrom) {
				params.startDate = dateFrom;
			}
			if (dateTo) {
				params.endDate = dateTo;
			}

			const response = await salesAPI.getAll(params);
			const salesData = response.sales || [];
			const total = response.total || 0;

			// Map sales to invoices
			const mappedInvoices: Invoice[] = salesData.map((sale: any) => {
				const customerName = sale.customer
					? `${sale.customer.firstName || ""} ${sale.customer.lastName || ""}`.trim()
					: "-";

				// Determine status based on payment status and due date
				let status: "paid" | "pending" | "partial" | "overdue" | "cancelled" = "pending";
				if (sale.status === "cancelled") {
					status = "cancelled";
				} else if (sale.paymentStatus === "paid") {
					status = "paid";
				} else if (sale.paymentStatus === "partial") {
					status = "partial";
				} else if (sale.dueDate && new Date(sale.dueDate) < new Date() && sale.paymentStatus !== "paid") {
					status = "overdue";
				} else {
					status = "pending";
				}

				return {
					id: sale.id,
					invoiceNumber: sale.invoiceNumber || sale.reference || sale.id,
					customerName,
					customer: sale.customer,
					amount: parseFloat(sale.totalAmount || 0),
					totalAmount: parseFloat(sale.totalAmount || 0),
					status,
					paymentStatus: sale.paymentStatus,
					splitCount: sale.splitChildren?.length ?? 0,
					dueDate: sale.dueDate || sale.createdAt,
					createdAt: sale.createdAt,
				};
			});

			setInvoices(mappedInvoices);
			setTotalInvoices(total);
			setTotalPages(Math.ceil(total / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			setError(t("invoices.errors.fetchFailed"));
			console.error("Invoices fetch error:", err);
			toast({
				title: t("common.error"),
				description: t("invoices.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteInvoice = async (invoiceId: number | string) => {
		if (await confirmAction(t("invoices.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await salesAPI.delete(invoiceId);
				setInvoices(prev => prev.filter(invoice => invoice.id !== invoiceId));
				setTotalInvoices(prev => prev - 1);
				toast({
					title: t("invoices.messages.deleteSuccess"),
					description: t("invoices.messages.deleteSuccessDescription"),
				});
				// Refresh the list
				fetchInvoices();
			} catch (err) {
				toast({
					title: t("invoices.messages.deleteError"),
					description: t("invoices.messages.deleteErrorDescription"),
					variant: "destructive",
				});
			}
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			paid: {
				variant: "success",
				text: t("invoices.status.paid"),
			},
			pending: {
				variant: "warning",
				text: t("invoices.status.pending"),
			},
			partial: {
				variant: "default",
				text: t("invoices.status.partial"),
			},
			overdue: {
				variant: "danger",
				text: t("invoices.status.overdue"),
			},
			cancelled: {
				variant: "destructive",
				text: t("invoices.status.cancelled"),
			},
		};

		const config = statusConfig[status as keyof typeof statusConfig];
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	if (isLoading && currentPage === 1) {
		return <Loading />;
	}

	return (
		<div className={`space-y-6 ${isRTL ? "text-right" : "text-left"}`}>
			{/* Filters and Search */}
			{!filters && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between py-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<Filter className="h-5 w-5" />
							{t("invoices.searchAndFilters")}
						</CardTitle>
						<Button
							variant="link"
							className="text-destructive"
							onClick={() => {
								setSearchTerm("");
								setSelectedStatus("");
								setDateFrom("");
								setDateTo("");
								setCurrentPage(1);
							}}
						>
							{t("invoices.clearFilters")}
						</Button>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
							<div className="relative">
								<Search
									className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`}
								/>
								<Input
									placeholder={t("invoices.searchPlaceholder")}
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									className={isRTL ? "pr-10" : "pl-10"}
								/>
							</div>

							<select
								value={selectedStatus}
								onChange={e => setSelectedStatus(e.target.value)}
								className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">{t("invoices.allStatuses")}</option>
								<option value="paid">{t("invoices.status.paid")}</option>
								<option value="partial">{t("invoices.status.partial")}</option>
								<option value="pending">{t("invoices.status.pending")}</option>
								<option value="overdue">{t("invoices.status.overdue")}</option>
								<option value="cancelled">{t("invoices.status.cancelled")}</option>
							</select>

							<Input
								type="date"
								placeholder={t("invoices.fromDate")}
								value={dateFrom}
								onChange={e => setDateFrom(e.target.value)}
							/>

							<Input
								type="date"
								placeholder={t("invoices.toDate")}
								value={dateTo}
								onChange={e => setDateTo(e.target.value)}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Invoices Table */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div className="space-y-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<FileText className="w-6 h-6 shrink-0" />
							<span>{t("invoices.title")}</span>
						</CardTitle>
						<CardDescription>{t("invoices.description")}</CardDescription>
					</div>
					{!filters && (
						<PermissionGate permission="sales.create">
							<Button className="flex items-center gap-2" onClick={() => navigate("/sales/new")}>
								<Plus className="h-4 w-4" />
								{t("invoices.newInvoice")}
							</Button>
						</PermissionGate>
					)}
				</CardHeader>
				<CardContent>
					<div className="record-surface">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("invoices.table.number")}</TableHead>
									<TableHead>{t("invoices.table.invoiceNumber")}</TableHead>
									<TableHead>{t("invoices.table.customerName")}</TableHead>
									<TableHead className="text-center">{t("invoices.table.amount")}</TableHead>
									<TableHead className="text-center">{t("invoices.table.status")}</TableHead>
									<TableHead className="text-center">{t("invoices.table.dueDate")}</TableHead>
									<TableHead className="text-center">{t("invoices.table.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!isLoading && invoices.length === 0 && (
									<TableRow>
										<TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
											{t("common.noData")}
										</TableCell>
									</TableRow>
								)}
								{invoices.map(invoice => (
									<TableRow key={invoice.id}>
										<TableCell>{invoice.id}</TableCell>
										<TableCell className="font-medium">
											<div className="flex flex-col items-start gap-1">
												<span>{invoice.invoiceNumber}</span>
												{invoice.splitCount ? (
													<Badge
														variant="outline"
														className="gap-1 border-primary/25 bg-primary/5 text-[10px] text-primary"
													>
														<Users className="h-3 w-3" />
														{t("sales.split.count", { count: invoice.splitCount })}
													</Badge>
												) : null}
											</div>
										</TableCell>
										<TableCell>{invoice.customerName}</TableCell>
										<TableCell className="text-center font-medium">
											<Price value={invoice.amount} currency={currency} />
										</TableCell>
										<TableCell className="text-center">{getStatusBadge(invoice.status)}</TableCell>
										<TableCell className="text-center text-muted-foreground">
											{formatDate(invoice.dueDate)}
										</TableCell>
										<TableCell className="text-center">
											<div className="flex items-center justify-center gap-2">
												<PermissionGate permission="sales.read">
													<Button
														variant="outline"
														title={t("common.view")}
														aria-label={t("common.view")}
														size="icon"
														onClick={() => navigate(`/invoices/${invoice.id}`)}
													>
														<Eye className="h-4 w-4" />
													</Button>
												</PermissionGate>
												{!invoice.splitCount ? (
													<PermissionGate permission="sales.update">
														<Button
															variant="outline"
															title={t("common.edit")}
															aria-label={t("common.edit")}
															size="icon"
															onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
														>
															<Edit className="h-4 w-4 text-primary" />
														</Button>
													</PermissionGate>
												) : null}
												{!invoice.splitCount ? (
													<PermissionGate permission="sales.delete">
														<Button
															variant="outline"
															title={t("common.delete")}
															aria-label={t("common.delete")}
															size="icon"
															onClick={() => handleDeleteInvoice(invoice.id)}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</PermissionGate>
												) : null}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalInvoices}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName="invoices.invoices"
			/>

			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<FileText className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("invoices.errorLoading")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchInvoices}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default InvoicesPage;
